import axiosInstance from "../../../middlewares/axiosInstance";
import {
  APIResponse,
  ListPOsRequest,
  ListPOsResponse,
  GetCompletedRFQsRequest,
  GetCompletedRFQsResponse,
  CheckRFQPORequest,
  CheckRFQPOResponse,
  CreatePORequest,
  CreatePOResponse,
  UpdatePORequest,
  GetPORequest,
  SubmitPORequest,
  ApprovePORequest,
  RejectPORequest,
  OrderPORequest,
  ReceivePORequest,
  DeletePORequest,
  GetPOActivityLogsResponse,
  PurchaseOrder,
  POActivityLog,
} from "../types/po.types";

class POService {
  private baseUrl = "/business-admin/po";

  // List all Purchase Orders
  async listPOs(params: ListPOsRequest): Promise<APIResponse<ListPOsResponse>> {
    const response = await axiosInstance.post(`${this.baseUrl}/list`, params);

    // Handle the nested data structure from backend
    if (response.data?.success && response.data?.data?.data) {
      const transformedResponse = {
        success: response.data.success,
        data: {
          purchase_orders: response.data.data.data,
          totalCount: response.data.data.metadata?.total?.value || 0,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          totalPages: Math.ceil(
            (response.data.data.metadata?.total?.value || 0) / params.pageSize
          ),
        },
      };
      return transformedResponse;
    }

    return response.data;
  }

  // Get available completed RFQs for PO creation
  async getCompletedRFQs(
    params: GetCompletedRFQsRequest
  ): Promise<APIResponse<GetCompletedRFQsResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/available-rfqs`,
      params
    );

    // Handle the nested data structure from backend
    if (response.data?.success && response.data?.data?.data) {
      const transformedResponse = {
        success: response.data.success,
        data: {
          rfqs: response.data.data.data,
          totalCount:
            response.data.data.total ||
            response.data.data.metadata?.total?.value ||
            0,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          totalPages: Math.ceil(
            (response.data.data.total ||
              response.data.data.metadata?.total?.value ||
              0) / params.pageSize
          ),
        },
      };
      return transformedResponse;
    }

    return response.data;
  }

  // Check if RFQ already has a PO
  async checkRFQPO(
    params: CheckRFQPORequest
  ): Promise<APIResponse<CheckRFQPOResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/check-rfq-po`,
      params
    );
    return response.data;
  }

  // Create new PO from completed RFQ
  async createPO(
    data: CreatePORequest
  ): Promise<APIResponse<CreatePOResponse>> {
    const response = await axiosInstance.post(`${this.baseUrl}/create`, data);
    return response.data;
  }

  // Update PO (only in draft status)
  async updatePO(data: UpdatePORequest): Promise<APIResponse<PurchaseOrder>> {
    const response = await axiosInstance.post(`${this.baseUrl}/update`, data);
    return response.data;
  }

  // Get single PO by ID
  async getPO(params: GetPORequest): Promise<APIResponse<PurchaseOrder>> {
    const response = await axiosInstance.post(`${this.baseUrl}/get`, params);
    return response.data;
  }

  // Submit PO for approval
  async submitPO(params: SubmitPORequest): Promise<APIResponse<PurchaseOrder>> {
    const response = await axiosInstance.post(`${this.baseUrl}/submit`, params);
    return response.data;
  }

  // Approve PO (admin/manager only)
  async approvePO(
    params: ApprovePORequest
  ): Promise<APIResponse<PurchaseOrder>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/approve`,
      params
    );
    return response.data;
  }

  // Reject PO (admin/manager only)
  async rejectPO(params: RejectPORequest): Promise<APIResponse<PurchaseOrder>> {
    const response = await axiosInstance.post(`${this.baseUrl}/reject`, params);
    return response.data;
  }

  // Order PO (generate order agreement)
  async orderPO(params: OrderPORequest): Promise<APIResponse<PurchaseOrder>> {
    const response = await axiosInstance.post(`${this.baseUrl}/order`, params);
    return response.data;
  }

  // Receive PO items (mark as received)
  async receivePO(
    params: ReceivePORequest
  ): Promise<APIResponse<PurchaseOrder>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/receive`,
      params
    );
    return response.data;
  }

  // Delete PO (only in draft status)
  async deletePO(params: DeletePORequest): Promise<APIResponse<void>> {
    const response = await axiosInstance.post(`${this.baseUrl}/delete`, params);
    return response.data;
  }

  // Get PO activity logs (from embedded data for offline capability)
  getRFQActivityLogs(
    po: PurchaseOrder,
    pageIndex: number = 0,
    pageSize: number = 10
  ): GetPOActivityLogsResponse {
    const activities = po.activity_logs || [];
    const totalCount = activities.length;
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const pageActivities = activities
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(startIndex, endIndex);

    return {
      activities: pageActivities,
      totalCount,
      pageIndex,
      pageSize,
      totalPages: Math.ceil(totalCount / pageSize),
    };
  }

  // Helper method to check if user can edit PO
  canEditPO(po: PurchaseOrder, userId: string): boolean {
    return po.status === "draft" && po.created_by === userId;
  }

  // Helper method to check if user can submit PO
  canSubmitPO(po: PurchaseOrder, userId: string): boolean {
    return po.status === "draft" && po.created_by === userId;
  }

  // Helper method to check if user can approve/reject PO
  canApprovePO(po: PurchaseOrder, userRole: string): boolean {
    return (
      po.status === "submitted" &&
      (userRole === "admin" || userRole === "manager")
    );
  }

  // Helper method to check if user can order PO
  canOrderPO(po: PurchaseOrder, userRole: string): boolean {
    return (
      po.status === "approved" &&
      (userRole === "admin" || userRole === "manager")
    );
  }

  // Helper method to check if user can receive PO
  canReceivePO(po: PurchaseOrder): boolean {
    return po.status === "ordered";
  }

  // Helper method to check if user can delete PO
  canDeletePO(po: PurchaseOrder, userId: string): boolean {
    return po.status === "draft" && po.created_by === userId;
  }
}

export const poService = new POService();
